import { cxEn } from './cx.en';

export const en = {
  nav: {
    services: "Services",
    about: "About",
    tech: "Technology",
    team: "Team",
    blog: "Insights",
    cx: "CX Services",
    contact: "Contact",
    getInTouch: "Start a Project",
  },
  hero: {
    eyebrow: "Neural AI · Live System",
    headline: "We Architect the AI‑Powered Future.",
    highlightWord: "Architect",
    subtitle: "Custom LLMs, machine learning systems, and world-class web products — engineered with precision for businesses ready to lead their industry.",
    description: "Custom LLMs, machine learning systems, and world-class web products — engineered with precision for businesses ready to lead their industry.",
    cta1: "Explore Services",
    cta2: "✦ Try AI Live",
    scrollLabel: "Scroll to explore",
    scroll: "Scroll to explore",
  },
  ticker: {
    items: ["LLM Development", "Generative AI", "RAG Systems", "Machine Learning", "Computer Vision", "Web Development", "AI Automation", "Neural Networks", "NLP Solutions", "Data Science", "AI Consulting", "Prompt Engineering", "Agentic AI", "GEO Optimization", "Multi-Agent Systems"],
  },
  services: {
    tag: "What We Do",
    title: "Services Built\nfor the AI Era",
    subtitle: "From intelligent automation to immersive digital products — we build the tools that let your business operate at a level your competitors can't match.",
    items: [
      { num: "01", title: "LLM & Generative AI", description: "Custom large language models, RAG pipelines, and generative systems trained on your data and deployed in your infrastructure.", icon: "brain", tags: ["ADDIS Bot", "RAG", "Fine-tuning", "NLP"] },
      { num: "02", title: "Machine Learning", description: "End-to-end ML pipelines — from data engineering to production-grade model deployment, monitoring, and continuous optimization.", icon: "cpu", tags: ["PyTorch", "TensorFlow", "MLOps"] },
      { num: "03", title: "Web Development", description: "High-performance, architecturally exceptional web applications. From SaaS platforms to enterprise systems — beautiful, fast, and built to scale.", icon: "code", tags: ["Next.js", "React", "Supabase"] },
      { num: "04", title: "Computer Vision", description: "Image recognition, object detection, and video intelligence — visual AI integrated seamlessly into your products and operational workflows.", icon: "eye", tags: ["YOLO", "OpenCV", "Vision API"] },
      { num: "05", title: "AI Automation", description: "Intelligent agents and multi-step orchestration that transform repetitive workflows into autonomous, self-optimizing systems.", icon: "zap", tags: ["LangChain", "Agents", "n8n"] },
      { num: "06", title: "AI Strategy & Consulting", description: "Roadmapping, technology selection, team training, and hands-on implementation guidance. We help you navigate AI adoption with confidence.", icon: "lightbulb", tags: ["Strategy", "Training", "Audit"] },
    ],
  },
  numbers: {
    items: [
      { value: 18200, suffix: "", label: "Hours Saved Annually" },
      { value: 409, suffix: "K+", label: "€ Recovered Value" },
      { value: 9, suffix: ".1×", label: "ROI Multiplier" },
      { value: 2, suffix: " mo", label: "Payback Period" },
    ],
  },
  about: {
    tag: "Our Philosophy",
    title: "Our Vision.\nYour Way.",
    story: "ADD Individual Solutions was founded on a belief that AI shouldn't be a luxury reserved for tech giants. We build the same calibre of intelligent systems for ambitious businesses of all sizes — with the same engineering rigour, the same architectural depth, and the same obsession with outcomes.",
    story2: "Based in Pyla, Cyprus, we operate across Europe and globally. Our multidisciplinary team combines deep AI research, software craftsmanship, and UX expertise to deliver products that don't just work — they set a new bar.",
    accentVal: "3+",
    accentLabel: "Years\nBased\nCyprus",
    quote: "\"We don't follow trends in AI — we set the standard for what thoughtful, production-grade AI looks like.\"",
    quoteAttr: "ADD Leadership Team · Pyla, Cyprus",
    pillars: [
      { icon: "⚡", title: "Engineered for Production", description: "Every system we build is designed to scale, monitored in real-time, and built to last." },
      { icon: "🎯", title: "Outcome Obsessed", description: "We measure success in business results, not lines of code or feature checklists." },
      { icon: "🌍", title: "EU Anchored, Globally Deployed", description: "Cyprus-based for EU-grade compliance. Deployed everywhere." },
      { icon: "🤝", title: "True Partnership", description: "Not vendors. Not agencies. We're the technical co-founders you never had." },
    ],
    location: "Pyla, Cyprus",
  },
  techStack: {
    tag: "Technology Stack",
    title: "Tools We Master",
    subtitle: "We don't chase every new framework. We master the tools that matter and deploy them with depth and discipline.",
    categories: [
      { label: "AI & LLMs", title: "Intelligence Layer", items: ["ADDIS Bot", "Custom LLMs", "Llama 3 / Mistral", "LangChain · LlamaIndex", "Pinecone · pgvector", "Hugging Face", "CrewAI"] },
      { label: "ML / Data", title: "Learning Systems", items: ["PyTorch · TensorFlow", "scikit-learn", "CUDA / GPU compute", "Pandas · Polars", "MLflow · Weights & Biases", "Apache Spark"] },
      { label: "Frontend / Backend", title: "Product Layer", items: ["Next.js 15 · React", "TypeScript", "Node.js · FastAPI", "PostgreSQL · Supabase", "Tailwind CSS", "GraphQL · tRPC"] },
      { label: "Cloud / Infrastructure", title: "Scale Layer", items: ["AWS · GCP · Azure", "Vercel · Cloudflare", "Docker · Kubernetes", "GitHub Actions CI/CD", "Terraform · Pulumi", "Grafana · Datadog"] },
    ],
  },
  process: {
    tag: "How We Work",
    title: "From First Call to\nLive in Production",
    subtitle: "A process that respects your time, your vision, and your budget — while never compromising on quality.",
    steps: [
      { num: "01", title: "Discovery", description: "Deep-dive into your business, goals, and technical landscape. We map the full picture before writing a line." },
      { num: "02", title: "Architecture", description: "Design the optimal AI and technical system. Proof of concept, feasibility, and tech stack selection." },
      { num: "03", title: "Development", description: "Agile sprints, transparent progress, and continuous integration. Results visible from week one." },
      { num: "04", title: "Launch & Evolve", description: "Production deployment, monitoring, and ongoing optimization. We stay to ensure the system grows with you." },
    ],
  },
  playground: {
    tag: "Live Demo",
    title: "Try Our AI",
    titleAccent: "Right Now",
    subtitle: "No signup. No waiting. Select a use case and experience what ADD can build for your business — powered by ADDIS Bot.",
    topbarTitle: "ADDIS BOT PLAYGROUND — LIVE DEMO",
    statusLabel: "ADDIS Bot · Connected",
    sidebarLabel: "Use Case",
    examplesTitle: "Try These",
    placeholder: "Ask anything about AI, code, data, or ADD's services...",
    disclaimer: "Powered by ADDIS Bot · ADD Individual Solutions",
    modes: {
      general: "General AI Chat",
      code: "Code Generator",
      analysis: "Data Analysis",
      content: "Content Writer",
      rag: "RAG Demo",
    },
    badges: { general: "Live" },
    examples: [
      "What is Retrieval-Augmented Generation?",
      "Write a Python function to classify text",
      "Explain transformers in simple terms",
      "How can AI reduce my business costs?",
      "What LLM should I use for my startup?",
    ],
    greetings: {
      general: "👋 Hello! I'm ADDIS Bot — the live AI assistant of ADD Individual Solutions. Ask me anything about AI, technology, or how ADD can help your business.",
      code: "💻 Code mode active. Describe what you want to build and I'll generate production-ready code for you.",
      analysis: "📊 Data Analysis mode. Share your data questions or describe your dataset and I'll help you extract insights.",
      content: "✍️ Content Writer mode. Tell me your topic, audience, and tone — I'll write compelling content.",
      rag: "🔍 RAG Demo mode. I'm now connected to ADD's knowledge base. Ask about our projects, capabilities, or technical docs.",
    },
    systemPrompts: {
      general: "You are ADDIS Bot, the AI assistant of ADD Individual Solutions — a Cyprus-based AI engineering company. You are helpful, knowledgeable about AI, machine learning, web development, and business automation. Be concise but thorough. Use markdown formatting when appropriate. Always respond in the language the user writes in.",
      code: "You are ADDIS Bot in Code Generator mode. You write clean, production-ready code with comments. Always include the programming language in code blocks. Provide explanations after code samples. Always respond in the language the user writes in.",
      analysis: "You are ADDIS Bot in Data Analysis mode. You help users understand data, suggest ML models, and provide analytical frameworks. Use structured formatting with headers and bullet points. Always respond in the language the user writes in.",
      content: "You are ADDIS Bot in Content Writer mode. You write compelling, professional marketing and business content. Adapt tone to the user's request. Use markdown formatting for structure. Always respond in the language the user writes in.",
      rag: "You are ADDIS Bot in RAG Demo mode. You answer as if you have access to ADD Individual Solutions' knowledge base. ADD is based in Pyla, Cyprus, has delivered 150+ projects across 12 countries with 98% client satisfaction. Services: LLM & Generative AI, Machine Learning, Web Development, Computer Vision, AI Automation, AI Strategy & Consulting. Tech stack includes ADDIS Bot, PyTorch, TensorFlow, Next.js, React, Supabase, LangChain, and more. Always cite \"[Source: ADD Knowledge Base]\" in responses. Always respond in the language the user writes in.",
    },
    errorMessage: "⚠️ I'm temporarily unable to respond. Please try again in a moment — our AI systems are being updated.",
    gate: {
      title: "Session limit reached",
      message: "You've explored {count} conversations in this session — thank you for trying ADDIS Bot. To unlock unlimited access or discuss a custom AI solution for your business, let's connect.",
      cta1: "Schedule a Consultation",
      cta2: "Contact Us",
    },
    gateRestricted: {
      message: "This is a limited demo available outside Europe. For the full ADDIS Bot experience, visit us from Europe or schedule a personalised consultation.",
    },
  },
  roi: {
    tag: "ROI Calculator",
    title: "What Could AI",
    titleAccent: "Save You?",
    subtitle: "Adjust the parameters to your business reality and see the projected impact of AI implementation — calculated from real client data across industries.",
    profileTitle: "Your Business Profile",
    resultsTitle: "Projected Annual Impact",
    industryLabel: "Industry",
    teamLabel: "Team Size",
    teamUnit: "employees",
    tasksLabel: "Repetitive Tasks",
    tasksUnit: "of workday",
    salaryLabel: "Avg. Salary",
    salaryUnit: "per year",
    automationLabel: "AI Automation Target",
    industries: ["E-Commerce", "Finance", "Healthcare", "SaaS", "Legal", "Media", "Logistics", "Other"],
    autoTargets: ["Conservative", "Moderate", "Aggressive"],
    hoursSaved: "Hours Saved Per Year",
    hoursSavedSub: "Across your entire team",
    costSavings: "Cost Savings",
    costSavingsSub: "In recovered productivity value",
    roiMultiplier: "ROI Multiplier",
    roiMultiplierSub: "Return on AI investment (avg. implementation cost)",
    paybackPeriod: "Payback Period",
    paybackSub: "Time to recoup full AI investment",
    months: "months",
    years: "years",
    hrs: "hrs",
    ctaText: "Get a detailed, personalised ROI report for your specific business case — free, tomorrow at 08:00 AM.",
    ctaButton: "Get My Free ROI Report →",
    nameLabel: "Your name",
    namePlaceholder: "Full name",
    emailLabel: "Your email",
    emailPlaceholder: "you@company.com",
    sending: "Sending...",
    confirmTitle: "Report Scheduled!",
    confirmMessage: "You'll receive a detailed, personalised ROI report — free, tomorrow morning at 08:00 AM. Check your inbox!",
    confirmAnother: "Calculate another ROI →",
  },
  configurator: {
    tag: "Find Your Solution",
    title: "What Does Your Business",
    titleAccent: "Actually Need?",
    subtitle: "Answer 4 quick questions and we'll match you with the perfect AI solution — and show you exactly how we'd build it.",
    stepTitles: ["Your Industry", "Your Challenge", "Team & Scale", "Timeline", "Your Details", "Your Solution"],
    back: "← Back",
    skip: "Skip →",
    continue: "Continue →",
    seeResult: "See My Solution →",
    submitButton: "Get My Solution →",
    submitting: "Sending...",
    startProject: "Start This Project →",
    contactStep: {
      title: "Almost there! Tell us how to reach you.",
      hint: "We'll send you a detailed proposal based on your answers.",
      nameLabel: "Your name",
      namePlaceholder: "Full name",
      emailLabel: "Your email",
      emailPlaceholder: "you@company.com",
    },
    confirmation: {
      title: "Thank you!",
      message: "We've received your answers. You'll receive a detailed AI solution blueprint — free, tomorrow morning at 08:00 AM. We'll recommend the perfect AI solution and show you exactly how we'd build it.",
      another: "Configure another solution →",
    },
    steps: [
      {
        title: "What's your industry?",
        hint: "We'll tailor our recommendation to your sector's specific AI use cases.",
        cards: [
          { icon: "🏦", title: "Finance & Banking", sub: "Risk, fraud detection, reporting" },
          { icon: "🛒", title: "E-Commerce & Retail", sub: "Recommendations, support, inventory" },
          { icon: "🏥", title: "Healthcare", sub: "Diagnostics, records, scheduling" },
          { icon: "💻", title: "Technology & SaaS", sub: "Dev tools, support, analytics" },
          { icon: "⚖️", title: "Legal & Compliance", sub: "Document review, contract AI" },
          { icon: "🌐", title: "Other Industry", sub: "Custom AI for your sector" },
        ],
      },
      {
        title: "What's your main challenge?",
        hint: "What problem keeps you up at night? We'll focus our solution there.",
        cards: [
          { icon: "⚙️", title: "Automating Manual Work", sub: "Too many repetitive tasks consuming team time and resources" },
          { icon: "📊", title: "Extracting Business Insights", sub: "Sitting on data but not able to act on it effectively" },
          { icon: "💬", title: "Customer Experience", sub: "Support, engagement, and personalisation at scale" },
          { icon: "🚀", title: "Building AI Products", sub: "Want to add AI capabilities to your own software" },
        ],
      },
      {
        title: "What's your team and budget?",
        hint: "This helps us recommend the right scope and approach.",
        cards: [
          { icon: "🌱", title: "Startup", sub: "1–20 people, MVP focus" },
          { icon: "📈", title: "Scale-up", sub: "20–100 people, growth stage" },
          { icon: "🏢", title: "Mid-Market", sub: "100–500, established ops" },
          { icon: "🏙️", title: "Enterprise", sub: "500+ complex requirements" },
        ],
      },
      {
        title: "When do you need results?",
        hint: "Honest timelines let us plan the right architecture from day one.",
        cards: [
          { icon: "⚡", title: "ASAP", sub: "MVP in 2–4 weeks, iterate fast" },
          { icon: "📅", title: "This Quarter", sub: "Full solution in 1–3 months" },
          { icon: "🗺️", title: "Strategic Rollout", sub: "Phased approach over 3–12 months" },
        ],
      },
    ],
    solutions: {
      automation: { title: "Intelligent Process Automation Suite", emoji: "⚙️", tags: ["RPA + AI", "Workflow Automation", "LLM Extraction", "Dashboard"] },
      insights: { title: "AI Analytics & Business Intelligence Platform", emoji: "📊", tags: ["Data Pipeline", "ML Predictions", "NLP Reports", "Real-time Dash"] },
      customer: { title: "Enterprise AI Customer Experience System", emoji: "💬", tags: ["LLM Chatbot", "RAG Knowledge Base", "Multi-channel", "CRM Integration"] },
      product: { title: "AI-Powered Product Feature Suite", emoji: "🚀", tags: ["LLM API Integration", "Embeddings", "Custom Models", "SDK"] },
    },
    solutionBodies: {
      automation: "We recommend an AI-powered automation platform combining LLMs for document processing, intelligent routing, and custom API integrations — eliminating {pct} of your manual work within {time}.",
      insights: "We'll build a machine learning-powered analytics system that connects your data sources, surfaces hidden patterns, and generates executive summaries automatically. Includes predictive models tailored to your industry benchmarks.",
      customer: "A production-grade AI customer system — RAG-powered chatbot trained on your knowledge base, seamlessly integrated with your existing CRM. Handles {volume} conversations simultaneously, 24/7.",
      product: "We'll architect and build AI features directly into your product — from recommendation engines to generative capabilities. Full SDK, documentation, and scalable infrastructure included. Timeline: {timeline}.",
    },
  },
  team: {
    tag: "The People",
    title: "Meet the Minds\nBehind ADD.IS",
    subtitle: "A small, elite team of engineers, researchers, and designers — united by an obsession with building things that actually work extraordinarily well.",
    members: [
      { name: "Founder & CEO", role: "Chief Executive Officer", bio: "Visionary leader with deep expertise in AI strategy and enterprise transformation." },
      { name: "Co-Founder & CTO", role: "Chief Technology Officer", bio: "10+ years architecting scalable systems. ML infrastructure and AI pipeline specialist." },
      { name: "UX/UI Lead", role: "Design Director", bio: "Making complex AI interfaces feel simple, human, and genuinely delightful to use." },
      { name: "ML Engineering Lead", role: "Head of Production", bio: "Ensuring every model and product meets the highest bar for reliability and performance." },
    ],
  },
  blog: {
    tag: "Insights",
    title: "Deep Dives on\nAI & Technology",
    allArticles: "All Articles →",
    readMore: "Read Article →",
    readShort: "Read →",
    posts: [
      { title: "The RAG Revolution: Building LLMs That Actually Know Your Business", excerpt: "How Retrieval-Augmented Generation eliminates hallucinations and grounds AI in your real data — with architecture patterns from 50+ enterprise deployments.", date: "Feb 2025", readTime: "12 min", category: "Machine Learning", featured: true },
      { title: "AI ROI in 2025: Real Numbers from Real Projects", excerpt: "How companies are measuring and maximising returns on AI investments — with actual data from our client portfolio.", date: "Jan 2025", readTime: "8 min", category: "Business", featured: false },
      { title: "Shipping LLM Apps to Production: The Checklist We Actually Use", excerpt: "Architecture decisions, evaluation frameworks, cost controls, and failure modes — the complete guide.", date: "Dec 2024", readTime: "10 min", category: "Engineering", featured: false },
    ],
  },
  newsletter: {
    tag: "Stay Ahead",
    title: "The ADD\nIntelligence Brief",
    subtitle: "Weekly: AI breakthroughs, practical engineering guides, and exclusive research from our team. 5,000+ professionals read it every Thursday.",
    placeholder: "you@email.com",
    cta: "Subscribe",
    note: "No spam. Unsubscribe anytime. EU privacy standards.",
    success: "✓ Subscribed! See you Thursday.",
  },
  contact: {
    tag: "Let's Talk",
    title: "Ready to Build\nSomething Remarkable?",
    subtitle: "Whether you have a fully formed brief or just a problem worth solving — reach out. First consultation is always free.",
    info: [
      { icon: "building", title: "Headquarters", text: "ADD Individual Solutions Ltd. · Pyla, Larnaca, Cyprus" },
      { icon: "mail", title: "Business Enquiries", text: "contact@add-individual-solutions.com", href: "mailto:contact@add-individual-solutions.com" },
      { icon: "mail", title: "Press & Information", text: "info@add-individual-solutions.com", href: "mailto:info@add-individual-solutions.com" },
      { icon: "globe", title: "Website", text: "add-individual-solutions.com", href: "https://add-individual-solutions.com" },
    ],
    form: {
      title: "Send a Message",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      service: "Service Interest",
      serviceOptions: ["Select a service...", "LLM & Generative AI", "Machine Learning", "Web Development", "AI Consulting", "Other"],
      message: "Your Message",
      send: "Send Message →",
      success: "✓ Message sent! We'll reply within 24 hours.",
    },
  },
  footer: {
    tagline: "Engineering AI systems and web products that transform businesses. Our Vision, Your Way.",
    services: {
      title: "Services",
      links: ["LLM Development", "Machine Learning", "Web Development", "AI Consulting"],
    },
    cx: {
      title: "CX Services",
      links: ["German-language support", "Multilingual support", "Back-office & data processing", "CX consulting & AI-enabled CX"],
    },
    company: {
      title: "Company",
      links: ["About Us", "Team", "Insights", "Experience Lab"],
    },
    legal: {
      title: "Legal",
      links: ["Privacy Policy", "Terms of Service", "Cookie Policy", "GDPR"],
    },
    copyright: "© 2025 ADD Individual Solutions Ltd. All rights reserved. Registered in Cyprus.",
    madeWith: "Made with ♥ in Cyprus — add-individual-solutions.com",
  },
  alex: {
    name: "Alex",
    status: "Online · ADD Team",
    greeting: "Hey there! I'm Alex from the ADD team. Got a question about AI, our work, or just curious about something? Fire away.",
    askName: "Hey! Before we dive in — what's your name?",
    askNameBeforeAnswer: "Hold on — before I answer that, what's your name?",
    askEmail: "Nice to meet you, {name}! Drop me your email so I know you're a real person and not a sneaky bot.",
    emailInvalid: "That doesn't look like a real email address — mind double-checking?",
    emailDisposable: "Nice try! But I need a real email, not a throwaway one. Just so I know you're human.",
    emailRefused: "I get it, privacy matters. But without an email I can't tell you apart from the bots crawling the internet. It's a one-time thing, promise.",
    emailSuspicious: "That email looks a bit... creative. I need a real one you actually check — no test addresses or keyboard mashing, please.",
    welcomeBack: "Hey {name}! Good to have you. What can I help with?",
    namePlaceholder: "Your name...",
    emailPlaceholder: "Your email...",
    chips: ["What do you actually build?", "Roughly, what does AI cost?", "How do I kick off a project?"],
    placeholder: "Type a message...",
    tag: "Chat",
    fallback: "Hmm, something tripped me up there. Drop us a line at contact@add-individual-solutions.com — the team will sort you out.",
    gate: {
      message: "Alright, I've hit my chat limit for today — but honestly, the real conversation starts with a call. The team would love to hear what you're working on.",
      cta1: "Book a Call",
      cta2: "Email Us",
    },
    gateRestricted: {
      message: "That's as far as I can go in this demo — but the team's got way more to show you. Book a quick call and we'll take it from there.",
    },
  },
  cookie: {
    message: "We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. By continuing to browse, you agree to our use of cookies.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    preferences: "Preferences",
    privacyLink: "Privacy Policy",
  },
  legalPages: {
    backToSite: "← Back to site",
    lastUpdated: "Last updated: February 2025",
    privacy: {
      title: "Privacy Policy",
      sections: [
        { heading: "1. Data Controller", content: "ADD Individual Solutions Ltd., registered in Pyla, Larnaca, Cyprus. Contact: contact@add-individual-solutions.com" },
        { heading: "2. Data We Collect", content: "We collect the following categories of personal data:", list: ["Contact information: name, email address, phone number when you submit forms or contact us.", "Usage data: pages visited, browser type, device information, IP address, referral source.", "Communication data: messages sent through our contact forms, newsletter subscriptions.", "Cookie data: see our Cookie Policy for details."] },
        { heading: "3. Legal Basis for Processing (GDPR Art. 6)", list: ["Consent: newsletter subscriptions, cookie consent.", "Contractual necessity: processing enquiries and project proposals.", "Legitimate interest: website analytics, fraud prevention, improving our services."] },
        { heading: "4. How We Use Your Data", list: ["To respond to your enquiries and provide our services.", "To send newsletters (with your explicit consent).", "To analyse website usage and improve our platform.", "To comply with legal obligations."] },
        { heading: "5. Data Sharing", content: "We do not sell your personal data. We may share data with:", list: ["Cloud infrastructure providers (Supabase, hosting services) for service delivery.", "Analytics tools for website improvement.", "Legal authorities when required by law."] },
        { heading: "6. Data Retention", content: "We retain personal data only as long as necessary for the purposes outlined above, or as required by law. Contact and project data is retained for up to 3 years after the last interaction. Analytics data is anonymised after 26 months." },
        { heading: "7. Your Rights (GDPR Art. 15–22)", content: "Under the GDPR, you have the right to:", list: ["Access your personal data", "Rectify inaccurate data", "Erase your data (\"right to be forgotten\")", "Restrict or object to processing", "Data portability", "Withdraw consent at any time", "Lodge a complaint with a supervisory authority"], after: "To exercise these rights, email us at contact@add-individual-solutions.com." },
        { heading: "8. International Transfers", content: "Your data may be processed in the EU/EEA. If transferred outside the EEA, we ensure appropriate safeguards (Standard Contractual Clauses) are in place per GDPR Art. 46." },
        { heading: "9. Security", content: "We implement appropriate technical and organisational measures to protect your data, including encryption in transit (TLS) and at rest, access controls, and regular security reviews." },
        { heading: "10. Changes", content: "We may update this policy periodically. Material changes will be communicated via our website." },
      ],
    },
    terms: {
      title: "Terms of Service",
      sections: [
        { heading: "1. Company Information", content: "These terms govern the use of services provided by ADD Individual Solutions Ltd., a company registered in Pyla, Larnaca, Cyprus. Contact: contact@add-individual-solutions.com" },
        { heading: "2. Services", content: "ADD Individual Solutions provides AI engineering, machine learning, web development, and consulting services. Specific deliverables, timelines, and fees are defined in individual project agreements." },
        { heading: "3. Intellectual Property", content: "Unless otherwise agreed in writing:", list: ["All intellectual property created during a project is transferred to the client upon full payment.", "ADD retains the right to use general methodologies, tools, and know-how developed during projects.", "Pre-existing IP of either party remains their own property."] },
        { heading: "4. Confidentiality", content: "Both parties agree to maintain the confidentiality of proprietary information shared during the engagement. This obligation survives termination of the agreement for a period of 2 years." },
        { heading: "5. Limitation of Liability", content: "To the maximum extent permitted by law, ADD's total liability is limited to the fees paid for the specific service giving rise to the claim. ADD is not liable for indirect, consequential, or incidental damages." },
        { heading: "6. Payment Terms", content: "Payment terms are specified in individual project proposals. Unless otherwise agreed, invoices are due within 14 days. Late payments may incur interest at the statutory rate." },
        { heading: "7. Termination", content: "Either party may terminate an engagement with 30 days' written notice. Work completed up to the termination date will be invoiced. Termination does not affect accrued rights or obligations." },
        { heading: "8. Website Use", content: "By using this website you agree not to:", list: ["Use automated systems to scrape or extract data.", "Attempt to gain unauthorised access to our systems.", "Use the website for any unlawful purpose."] },
        { heading: "9. Governing Law", content: "These terms are governed by the laws of the Republic of Cyprus. Any disputes shall be resolved by the courts of Larnaca, Cyprus, without prejudice to your rights under EU consumer protection law." },
        { heading: "10. Changes", content: "We reserve the right to update these terms. Continued use of our services constitutes acceptance of updated terms." },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      sections: [
        { heading: "1. What Are Cookies?", content: "Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and understand how you interact with it." },
        { heading: "2. Cookies We Use", subsections: [
          { subheading: "Essential Cookies", content: "Required for the website to function properly. These cannot be disabled.", list: ["add-lang: Stores your language preference (EN/RO). Duration: persistent.", "add-cookie-consent: Stores your cookie consent choice. Duration: 1 year."] },
          { subheading: "Analytics Cookies", content: "Help us understand how visitors interact with our website to improve the user experience.", list: ["Site analytics: Page views, visit duration, device type. Data is stored in our own infrastructure (Supabase) — not shared with third-party analytics platforms."] },
          { subheading: "Functional Cookies", content: "Enable enhanced functionality such as the AI playground session and newsletter preferences." },
        ] },
        { heading: "3. Third-Party Cookies", content: "We minimise the use of third-party cookies. Our website primarily uses first-party cookies and self-hosted analytics. If third-party services are integrated (e.g., embedded content), their respective cookie policies apply." },
        { heading: "4. Managing Cookies", content: "You can manage cookie preferences through:", list: ["Our cookie consent banner (shown on first visit).", "Your browser settings — most browsers allow you to block or delete cookies."], after: "Note: disabling essential cookies may affect website functionality." },
        { heading: "5. Legal Basis", content: "We use cookies based on your consent (GDPR Art. 6(1)(a)) for non-essential cookies, and legitimate interest (Art. 6(1)(f)) for essential cookies required for website operation, in compliance with the ePrivacy Directive (2002/58/EC)." },
        { heading: "6. Contact", content: "For questions about our cookie practices, contact us at contact@add-individual-solutions.com." },
      ],
    },
    gdpr: {
      title: "GDPR Compliance",
      sections: [
        { heading: "Our Commitment to Data Protection", content: "ADD Individual Solutions Ltd. is committed to protecting the privacy and security of personal data in accordance with the General Data Protection Regulation (EU) 2016/679 (\"GDPR\") and the Cyprus Data Protection Law (Law 125(I)/2018)." },
        { heading: "Data Controller", content: "ADD Individual Solutions Ltd.\nPyla, Larnaca, Cyprus\nEmail: contact@add-individual-solutions.com" },
        { heading: "Data Protection Principles", content: "We adhere to all GDPR data protection principles (Art. 5):", list: ["Lawfulness, fairness, and transparency: We process data lawfully and transparently.", "Purpose limitation: Data is collected for specified, explicit, and legitimate purposes.", "Data minimisation: We collect only the data necessary for each purpose.", "Accuracy: We take reasonable steps to ensure data is accurate and up to date.", "Storage limitation: Data is retained only as long as necessary.", "Integrity and confidentiality: Appropriate security measures protect personal data.", "Accountability: We maintain documentation of our processing activities."] },
        { heading: "Your Rights Under GDPR", content: "As a data subject, you have the following rights:", list: ["Right of access (Art. 15): Request a copy of your personal data.", "Right to rectification (Art. 16): Request correction of inaccurate data.", "Right to erasure (Art. 17): Request deletion of your data (\"right to be forgotten\").", "Right to restrict processing (Art. 18): Request limitation of data processing.", "Right to data portability (Art. 20): Receive your data in a structured, machine-readable format.", "Right to object (Art. 21): Object to processing based on legitimate interests.", "Right related to automated decision-making (Art. 22): Not be subject to decisions based solely on automated processing."] },
        { heading: "How to Exercise Your Rights", content: "Send your request to contact@add-individual-solutions.com. We will respond within 30 days as required by GDPR Art. 12(3). We may ask for identity verification before processing your request." },
        { heading: "Data Breach Notification", content: "In the event of a personal data breach, we will:", list: ["Notify the relevant supervisory authority within 72 hours (Art. 33).", "Notify affected individuals without undue delay if the breach poses a high risk to their rights (Art. 34)."] },
        { heading: "Supervisory Authority", content: "You have the right to lodge a complaint with the Commissioner for Personal Data Protection in Cyprus:\nOffice of the Commissioner for Personal Data Protection\nNicosia, Cyprus\nWebsite: www.dataprotection.gov.cy" },
        { heading: "Related Policies", links: ["Privacy Policy", "Cookie Policy", "Terms of Service"] },
      ],
    },
  },
  whyUs: {
    tag: "Why ADD.IS",
    title: "Why Companies\nChoose Us",
    subtitle: "Enterprise-grade AI engineering without the enterprise overhead. Here's how we compare.",
    tiers: [
      {
        label: "vs. Enterprise Giants",
        examples: "Infosys · Fractal · EXL",
        advantage: "Same engineering rigor. No bloated retainer. 2-month payback.",
        items: [
          { text: "Enterprise-grade architecture", us: true, them: true },
          { text: "Rapid 2-month payback period", us: true, them: false },
          { text: "Dedicated, senior-only team", us: true, them: false },
          { text: "No multi-year lock-in contracts", us: true, them: false },
          { text: "Transparent, usage-based pricing", us: true, them: false },
        ],
      },
      {
        label: "vs. Boutique AI Agencies",
        examples: "LeewayHertz · Cieden · Lazarev",
        advantage: "Measurable outcomes, not just prototypes — production-grade systems.",
        items: [
          { text: "Technical thought leadership", us: true, them: true },
          { text: "Measurable KPIs defined before kickoff", us: true, them: false },
          { text: "Full production deployment", us: true, them: false },
          { text: "EU data compliance built-in", us: true, them: false },
          { text: "Post-launch optimization & monitoring", us: true, them: false },
        ],
      },
      {
        label: "vs. Niche Automation Shops",
        examples: "API wrappers · low-code · Zapier agencies",
        advantage: "Full-stack infrastructure, not fragile API wrappers.",
        items: [
          { text: "Quick setup and delivery", us: true, them: true },
          { text: "Custom private LLM development", us: true, them: false },
          { text: "AWS / GCP / Azure infrastructure", us: true, them: false },
          { text: "Kubernetes & Terraform at scale", us: true, them: false },
          { text: "Agentic AI with SPAR framework", us: true, them: false },
        ],
      },
    ],
  },
  cx: cxEn,
};
